import { useEffect, useState } from 'preact/hooks';

import { GLOW_ATTRIBUTE } from '../components/TourSpotlightSlot';

const readTarget = (): string | null =>
  typeof document === 'undefined' ? null : document.body.getAttribute(GLOW_ATTRIBUTE);

/**
 * Whether the tour is currently spotlighting `target`, tracked reactively.
 *
 * The glow itself is pure CSS (a `group-data` variant off the attribute TourSpotlightSlot writes
 * on <body>), so most highlights need nothing here. This is for the cases where an element must
 * *mount* when the spotlight arrives rather than merely restyle — e.g. a highlighted affordance
 * whose Grommet Drop measures its target's position once and cannot recover if that target was
 * hidden (display:none) at mount. Gating the render on this hook mounts it fresh, so the Drop
 * measures a laid-out target.
 */
export const useIsTourSpotlight = (target: string): boolean => {
  const [isSpotlight, setIsSpotlight] = useState(() => readTarget() === target);

  useEffect(() => {
    const update = () => setIsSpotlight(readTarget() === target);
    update(); // the attribute may have changed between render and effect
    const observer = new MutationObserver(update);
    observer.observe(document.body, { attributes: true, attributeFilter: [GLOW_ATTRIBUTE] });
    return () => observer.disconnect();
  }, [target]);

  return isSpotlight;
};

export default useIsTourSpotlight;
